import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';

const gradientShift = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: var(--space-24) 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      ellipse at 30% 20%,
      rgba(81, 20, 97, 0.08) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 70% 80%,
      rgba(163, 82, 41, 0.06) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
`;

const Eyebrow = styled.span`
  display: inline-block;
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-brand-primary);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: var(--space-4);
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: var(--font-bold);
  line-height: 1.1;
  margin-bottom: var(--space-6);

  .gradient {
    background: linear-gradient(
      135deg,
      var(--color-brand-primary),
      var(--color-brand-secondary),
      var(--color-brand-primary)
    );
    background-size: 200% 200%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientShift} 5s ease infinite;
  }
`;

const Subtitle = styled.p`
  font-size: var(--text-xl);
  color: var(--color-text-secondary);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-8);
  max-width: 600px;

  @media (max-width: 768px) {
    font-size: var(--text-lg);
  }
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
`;

const Stats = styled.div`
  display: flex;
  gap: var(--space-12);
  margin-top: var(--space-16);
  padding-top: var(--space-8);
  border-top: 1px solid var(--color-border);

  @media (max-width: 768px) {
    gap: var(--space-8);
    flex-wrap: wrap;
  }
`;

const Stat = styled.div``;

const StatValue = styled.div`
  font-size: var(--text-3xl);
  font-weight: var(--font-bold);
  color: var(--color-text-primary);
`;

const StatLabel = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
`;

interface HeroProps {
  eyebrow?: string;
  title: React.ReactNode;
  subtitle: string;
  primaryCTA?: {
    label: string;
    to: string;
  };
  secondaryCTA?: {
    label: string;
    to: string;
  };
  showStats?: boolean;
}

export function Hero({
  eyebrow = 'Lancaster Systems Automation',
  title,
  subtitle,
  primaryCTA = { label: 'Explore Our Apps', to: '/products' },
  secondaryCTA = { label: 'Hire Our Team', to: '/contact' },
  showStats = true,
}: HeroProps) {
  return (
    <HeroSection>
      <Container>
        <HeroContent>
          <Eyebrow>{eyebrow}</Eyebrow>
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>

          <CTAGroup>
            <Button as={Link} to={primaryCTA.to} size="lg">
              {primaryCTA.label}
            </Button>
            <Button as={Link} to={secondaryCTA.to} size="lg" variant="secondary">
              {secondaryCTA.label}
            </Button>
          </CTAGroup>

          {showStats && (
            <Stats>
              <Stat>
                <StatValue>4</StatValue>
                <StatLabel>Apps Shipped</StatLabel>
              </Stat>
              <Stat>
                <StatValue>6+</StatValue>
                <StatLabel>Client Projects</StatLabel>
              </Stat>
            </Stats>
          )}
        </HeroContent>
      </Container>
    </HeroSection>
  );
}
